'use client'

import AgentService from '@/features/agent/agent-service'
import { COOPERA_COOKIE, IMPORTANT_INFORMATION_COOKIE, UPDATE_AGENT_EMAIL_COOKIE } from '@/features/agent/model/cookies'
import AuthService from '@/features/auth/auth-service'
import { TranslationBundles } from '@/features/sitecore/configs/translation-bundles'
import { useSitecoreState } from '@/features/sitecore/store/useSitecoreState'
import WebsphereService from '@/features/websphere/websphere-service'
import { User } from 'next-auth'
import { useState } from 'react'
import { checkTextCookie } from '../http-headers/actions/text-cookies'
import { useAsyncEffect } from './useAsyncEffect'
import { useModalState } from './useModalState'

export const useUserEntryModals = () => {
	const modal = useModalState()
	const [user, setUser] = useState<User | undefined>(undefined)
	const sitecoreStore = useSitecoreState((state) => state)

	useAsyncEffect(async () => {
		const session = await AuthService.session()
		setUser(session?.user)
	}, [])

	// Showing modals driven by cookies: updateAgentEmail, info
	useAsyncEffect(async () => {
		if (!user) return

		const cooperaActive = await WebsphereService.isFeatureEnabledFromStoretextCustom('cooperaEnabled')
		if (cooperaActive) {
			const cooperaCookieExists = await checkTextCookie(COOPERA_COOKIE.name)
			const cooperaHasValidContract = await AgentService.checkIfCooperaHasValidContract()
			if (cooperaHasValidContract && user.administrator && !cooperaCookieExists) {
				modal.openCooperaModal()
				return
			}
		}

		const updateAgentEmailCookieExists = await checkTextCookie(UPDATE_AGENT_EMAIL_COOKIE.name)
		if (!updateAgentEmailCookieExists) {
			modal.openUpdateEmailModal()
			return
		}

		if (user.additionalData.newConsentVersionToAccept) {
			modal.openNewConsentModal()
			return
		}

		const importantInformationCookieExists = await checkTextCookie(IMPORTANT_INFORMATION_COOKIE.name)
		if (!importantInformationCookieExists) {
			const [page, width, height] = sitecoreStore.getLabel(TranslationBundles.HOME, 'MSC-HOMEPAGE-URL_SIZE_IFRAME', ';;').split(';')
			if (page.includes('htm') || page.includes('html')) {
				modal.openImportantInformationsModal({ page }, { size: { width, height, modalSize: 'md' } })
				return
			}
		}
	}, [user])
}
